import { useThreadStore } from "@/store";
import {
  processTextContent,
  shouldShowInStreamingMessage,
} from "./messageUtils";
import { parseStorageFiles } from "./parsingUtils";

/**
 * Enhanced sorting function that handles various timestamp formats and edge cases
 */
export const sortMessagesByTimestamp = (messages) => {
  return [...messages].sort((a, b) => {
    // Helper function to get the most reliable timestamp
    const getTimestamp = (msg) => {
      // CRITICAL FIX: Always prioritize created_at if it exists
      if (msg.created_at) {
        return msg.created_at;
      }

      // For user messages, prioritize created_at as it's most reliable
      if (msg.role === "user") {
        return (
          msg.created_at ||
          msg.timestamp ||
          msg.updated_at ||
          "1970-01-01T00:00:00.000Z"
        );
      }

      // For assistant messages, use created_at if available, otherwise updated_at
      // But ensure we don't use updated_at if it's significantly newer than created_at
      // (which could indicate a later update, not the original creation time)
      const createdAt = msg.created_at || msg.timestamp;
      const updatedAt = msg.updated_at;

      if (createdAt && updatedAt) {
        const createdTime = new Date(createdAt).getTime();
        const updatedTime = new Date(updatedAt).getTime();

        // If updated_at is more than 10 seconds after created_at,
        // it's likely a later update, so use created_at
        if (updatedTime - createdTime > 10000) {
          return createdAt;
        }

        // Otherwise, use the earlier of the two timestamps
        return createdTime <= updatedTime ? createdAt : updatedAt;
      }

      return createdAt || updatedAt || "1970-01-01T00:00:00.000Z";
    };

    const timestampA = getTimestamp(a);
    const timestampB = getTimestamp(b);

    const dateA = new Date(timestampA);
    const dateB = new Date(timestampB);

    // Ensure valid dates
    if (isNaN(dateA.getTime())) {
      return 1; // Put invalid timestamps at the end
    }
    if (isNaN(dateB.getTime())) {
      return -1; // Put invalid timestamps at the end
    }

    const timeDiff = dateA.getTime() - dateB.getTime();

    // If timestamps are very close (within 100ms), use message creation order as tiebreaker
    if (Math.abs(timeDiff) < 100) {
      // For messages with very close timestamps, maintain user->assistant order
      if (a.role === "user" && b.role === "assistant") {
        return -1; // User message comes first
      }
      if (a.role === "assistant" && b.role === "user") {
        return 1; // User message comes first
      }

      // For assistant messages, use the actual timestamp difference instead of ID comparison
      // This ensures chronological order is maintained even for close timestamps
      if (a.role === "assistant" && b.role === "assistant") {
        return timeDiff; // Use the actual time difference
      }

      // If same role, use ID comparison as last resort for consistency
      return a.id.localeCompare(b.id);
    }

    return timeDiff;
  });
};

/**
 * Filter messages that should be displayed
 */
export const filterMessagesToDisplay = (messages) => {
  return (
    messages?.filter((msg) => {
      // Include non-streaming messages
      if (msg.isStreaming !== true) {
        return true;
      }

      // Include streaming messages that should be shown
      if (shouldShowInStreamingMessage(msg)) {
        return true;
      }

      // CRITICAL FIX: Include streaming messages with reasoning content
      // even if they don't have main content yet
      if (
        msg.isStreaming === true &&
        (msg.metadata?.reasoningContent ||
          msg.metadata?.isReasoningResponse === true ||
          msg.metadata?.inReasoningBlock === true)
      ) {
        return true;
      }

      // Include streaming messages with completed tool calls
      if (
        msg.tool_calls &&
        Array.isArray(msg.tool_calls) &&
        msg.tool_calls.length > 0
      ) {
        const hasCompletedToolCalls = msg.tool_calls.some((tc) => tc.result);
        if (hasCompletedToolCalls) {
          return true;
        }
      }

      return false;
    }) || []
  );
};

/**
 * Process messages for display with all necessary metadata
 */
export const processMessagesForDisplay = (
  messages,
  messageFileAttachments,
  isNewChatPath
) => {
  if (isNewChatPath && messages?.length === 0) {
    return [];
  }

  // Get currentMessageId from threadStore
  const { currentMessageId } = useThreadStore.getState();

  // Sort messages to ensure chronological order
  const sortedMessages = sortMessagesByTimestamp(messages || []);

  // Filter messages that should be displayed
  const messagesToProcess = filterMessagesToDisplay(sortedMessages);

  const processed = messagesToProcess?.map((message, index, allMessages) => {
    const isUserMessage = message?.role === "user";
    const isFirst = index === 0;
    const isLast = index === allMessages.length - 1;
    const isCurrentMessage = message.id === currentMessageId;
    const isStreamingMessage = shouldShowInStreamingMessage(message);

    const content = processTextContent(message);

    // Check for file attachments directly
    const hasFileAttachments =
      isUserMessage &&
      (!!messageFileAttachments[message.id] ||
        message.metadata?.has_files === true ||
        (message.metadata?.storage_files &&
          message.metadata.storage_files !== "[]"));

    return {
      message,
      content,
      isUserMessage,
      isFirst,
      isLast,
      isCurrentMessage,
      hasFileAttachments,
      isStreamingMessage,
    };
  });

  return processed;
};

/**
 * Extract file attachments from messages
 */
export const extractMessageFileAttachments = (messages) => {
  if (!messages || messages.length === 0) return {};

  const attachments = {};

  messages.forEach((message) => {
    if (!message?.id) return;

    // Don't render file attachments for assistant or tool messages
    if (message?.role === "assistant" || message?.role === "tool") return;

    // Don't render file attachments for messages with tool_calls
    const hasToolCalls =
      (message?.tool_calls && message.tool_calls.length > 0) ||
      (message.metadata?.tool_calls && message.metadata.tool_calls.length > 0);

    if (hasToolCalls) return;

    // Extract files from message metadata using shared utility
    const files = parseStorageFiles(message.metadata);
    if (files.length > 0) {
      attachments[message.id] = files;
    }
  });

  return attachments;
};
